import { useState, useEffect, useRef, useCallback } from "react";
import useTaskQueueStore from "./useTaskQueueStore";
import {
  getApiStatusCode,
  getApiPayload,
  parseTaskQueueErrorMessage,
  queueUiIdempotencyKey,
  TASK_QUEUE_DEFAULT_WIDTH,
  TASK_QUEUE_AUTO_OPEN_EVENT_TYPES,
} from "./agentSessionsPanelUtils";
import { isEnabled as isStreamDebugEnabled } from "../../utils/ChatStreamLogger";

export default function useTaskQueuePanel({
  selectedSessionId,
  isPublicMode,
  selectedPublicSessionToken,
  publicIdentityPayload,
  taskQueuePlannerUiEnabled,
  taskQueueOpenSignal,
  isNarrowQueueMode,
  resolvePrivateClientId,
  storeTaskQueueEventsBySessionId,
  storeSessionStreamStateBySessionId,
}) {
  const [taskQueuePanelOpen, setTaskQueuePanelOpen] = useState(false);
  const [taskQueuePanelWidth, setTaskQueuePanelWidth] = useState(
    TASK_QUEUE_DEFAULT_WIDTH
  );
  const [taskQueueSnapshot, setTaskQueueSnapshot] = useState(null);
  const [taskQueueHistory, setTaskQueueHistory] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [taskQueueLoading, setTaskQueueLoading] = useState(false);
  const [taskQueueApplying, setTaskQueueApplying] = useState(false);
  const [taskQueueError, setTaskQueueError] = useState("");
  const [taskQueueConnectionState, setTaskQueueConnectionState] =
    useState("idle");
  const [taskQueueRevisionSyncing, setTaskQueueRevisionSyncing] =
    useState(false);
  const [taskQueueExpandedMode, setTaskQueueExpandedMode] = useState(false);
  const [mobileQueueTab, setMobileQueueTab] = useState("chat");

  const lastHandledTaskQueueOpenSignalRef = useRef(taskQueueOpenSignal);
  const lastProcessedEventKeyRef = useRef(null);
  const loadTaskQueueSnapshotRef = useRef(null);
  const taskQueueSnapshotRef = useRef(taskQueueSnapshot);
  taskQueueSnapshotRef.current = taskQueueSnapshot;
  const selectedQueueIdRef = useRef(selectedQueueId);
  selectedQueueIdRef.current = selectedQueueId;
  const taskQueueRevisionSyncingRef = useRef(false);

  // --- Derived values ---

  const latestTaskQueueEvent =
    !isPublicMode &&
    selectedSessionId &&
    selectedSessionId !== "__pending__" &&
    storeTaskQueueEventsBySessionId
      ? storeTaskQueueEventsBySessionId[selectedSessionId] || null
      : null;

  const privateSessionStreamState =
    !isPublicMode &&
    selectedSessionId &&
    selectedSessionId !== "__pending__" &&
    storeSessionStreamStateBySessionId
      ? String(
          storeSessionStreamStateBySessionId[selectedSessionId] || "idle"
        )
          .trim()
          .toLowerCase()
      : "idle";

  // --- Callbacks ---

  const applyTaskQueueApiResponse = useCallback(
    (response, fallbackMessage, options = {}) => {
      const openWhenQueueAvailable = options?.openWhenQueueAvailable === true;
      const statusCode = getApiStatusCode(response);
      const payload = getApiPayload(response);
      const code = String(payload?.error?.code || "").trim().toUpperCase();
      const isQueueNotFound = code === "QUEUE_NOT_FOUND";
      if (statusCode >= 400) {
        if (isQueueNotFound) {
          setTaskQueueSnapshot(null);
          setTaskQueueError("");
          return null;
        }
        setTaskQueueError(
          parseTaskQueueErrorMessage(
            response,
            fallbackMessage || "Task queue request failed."
          )
        );
        if (payload && typeof payload === "object" && (payload.queue || payload.error)) {
          setTaskQueueSnapshot(payload);
        }
        return null;
      }
      const snapshot = payload && typeof payload === "object" ? payload : null;
      if (isQueueNotFound) {
        setTaskQueueSnapshot(null);
        setTaskQueueError("");
        return null;
      }
      setTaskQueueSnapshot(snapshot);
      setTaskQueueError(
        typeof snapshot?.error?.message === "string" ? snapshot.error.message : ""
      );
      if (snapshot?.queue && openWhenQueueAvailable) {
        setTaskQueuePanelOpen(true);
      }
      return snapshot;
    },
    []
  );

  const loadSessionQueueHistory = useCallback(
    async () => {
      if (isPublicMode) return;
      const clientId = resolvePrivateClientId();
      if (!clientId || !selectedSessionId || selectedSessionId === "__pending__") {
        setTaskQueueHistory([]);
        return;
      }
      try {
        const response = await useTaskQueueStore.getState().getSessionTaskQueueHistory(
          selectedSessionId,
          clientId
        );
        const payload = getApiPayload(response);
        const items = Array.isArray(payload?.items) ? payload.items : [];
        setTaskQueueHistory(items);
      } catch {
        // non-critical; history list may remain empty
      }
    },
    [isPublicMode, resolvePrivateClientId, selectedSessionId]
  );

  const loadTaskQueueSnapshot = useCallback(
    async ({ silent = false } = {}) => {
      const hasActiveSession =
        Boolean(selectedSessionId) && selectedSessionId !== "__pending__";
      if (!hasActiveSession) {
        setTaskQueueSnapshot(null);
        setTaskQueueHistory([]);
        setSelectedQueueId(null);
        setTaskQueueError("");
        setTaskQueueConnectionState("idle");
        return null;
      }
      if (!taskQueuePlannerUiEnabled) {
        setTaskQueueSnapshot(null);
        setTaskQueueHistory([]);
        setSelectedQueueId(null);
        setTaskQueueError("");
        setTaskQueueConnectionState("idle");
        return null;
      }

      if (!silent) {
        setTaskQueueLoading(true);
      }

      try {
        if (isPublicMode) {
          if (!selectedPublicSessionToken) {
            setTaskQueueSnapshot(null);
            setTaskQueueError("");
            return null;
          }
          const response = await useTaskQueueStore.getState().getPublicTaskQueue(
            selectedPublicSessionToken,
            publicIdentityPayload || {}
          );
          return applyTaskQueueApiResponse(response, "Unable to load public task queue.");
        }

        const clientId = resolvePrivateClientId();
        if (!clientId) {
          return null;
        }
        const response = await useTaskQueueStore.getState().getSessionTaskQueue(
          selectedSessionId,
          clientId
        );
        const result = applyTaskQueueApiResponse(response, "Unable to load task queue.");
        // Also refresh queue history list (non-blocking)
        loadSessionQueueHistory();
        // Track the active queue's ID — only on initial load
        if (result?.queue?.queueId && !selectedQueueIdRef.current) {
          setSelectedQueueId(result.queue.queueId);
        }
        return result;
      } finally {
        if (!silent) {
          setTaskQueueLoading(false);
        }
      }
    },
    [
      applyTaskQueueApiResponse,
      isPublicMode,
      loadSessionQueueHistory,
      publicIdentityPayload,
      resolvePrivateClientId,
      selectedPublicSessionToken,
      selectedSessionId,
      taskQueuePlannerUiEnabled,
    ]
  );
  loadTaskQueueSnapshotRef.current = loadTaskQueueSnapshot;

  const handleApplyTaskQueueAction = useCallback(
    async (actionPayload = {}) => {
      const action = String(actionPayload?.action || "").trim();
      if (!action) return null;
      const hasActiveSession =
        Boolean(selectedSessionId) && selectedSessionId !== "__pending__";
      if (!hasActiveSession) return null;

      const nextPayload = {
        ...actionPayload,
        idempotencyKey: queueUiIdempotencyKey(),
      };
      const currentRevision = Number(taskQueueSnapshot?.queue?.revision);
      if (Number.isFinite(currentRevision)) {
        nextPayload.ifRevision = currentRevision;
      }

      setTaskQueueApplying(true);
      try {
        if (isPublicMode) {
          if (!selectedPublicSessionToken) {
            setTaskQueueError("Missing public session token for task queue action.");
            return null;
          }
          const response = await useTaskQueueStore.getState().applyPublicTaskQueueAction(
            selectedPublicSessionToken,
            {
              ...(publicIdentityPayload || {}),
              ...nextPayload,
            }
          );
          return applyTaskQueueApiResponse(
            response,
            "Unable to apply public task queue action.",
            { openWhenQueueAvailable: true }
          );
        }

        const clientId = resolvePrivateClientId();
        if (!clientId) {
          setTaskQueueError("Missing client ID for task queue action.");
          return null;
        }
        const response = await useTaskQueueStore.getState().applySessionTaskQueueAction(
          selectedSessionId,
          {
            clientId,
            ...nextPayload,
          }
        );
        return applyTaskQueueApiResponse(response, "Unable to apply task queue action.", {
          openWhenQueueAvailable: true,
        });
      } finally {
        setTaskQueueApplying(false);
      }
    },
    [
      applyTaskQueueApiResponse,
      isPublicMode,
      publicIdentityPayload,
      resolvePrivateClientId,
      selectedPublicSessionToken,
      selectedSessionId,
      taskQueueSnapshot?.queue?.revision,
    ]
  );

  const handleSelectQueue = useCallback(
    (queueId) => {
      setSelectedQueueId(queueId);
      const match = taskQueueHistory.find(
        (s) => s?.queue?.queueId === queueId
      );
      if (match) {
        setTaskQueueSnapshot(match);
        setTaskQueueError(
          typeof match?.error?.message === "string" ? match.error.message : ""
        );
      }
    },
    [taskQueueHistory]
  );

  const handleOpenTaskQueuePanel = useCallback(() => {
    setTaskQueuePanelOpen(true);
    if (isNarrowQueueMode) {
      setMobileQueueTab("queue");
    }
    loadTaskQueueSnapshotRef.current?.({ silent: false });
  }, [isNarrowQueueMode]);

  const handleCloseTaskQueuePanel = useCallback(() => {
    setTaskQueuePanelOpen(false);
    setTaskQueueExpandedMode(false);
  }, []);

  const handleTaskQueueExpandedModeChange = useCallback((expanded) => {
    setTaskQueueExpandedMode(Boolean(expanded));
  }, []);

  // --- Effects ---

  // taskQueueOpenSignal effect
  useEffect(() => {
    if (!taskQueueOpenSignal) return;
    if (taskQueueOpenSignal === lastHandledTaskQueueOpenSignalRef.current) return;
    lastHandledTaskQueueOpenSignalRef.current = taskQueueOpenSignal;
    handleOpenTaskQueuePanel();
  }, [handleOpenTaskQueuePanel, taskQueueOpenSignal]);

  // Session-change reset + initial load
  useEffect(() => {
    const hasActiveSession =
      Boolean(selectedSessionId) && selectedSessionId !== "__pending__";
    // Always reset queue state when the session changes so stale data
    // from the previous session is never visible in the panel.
    setTaskQueueSnapshot(null);
    setTaskQueueHistory([]);
    setSelectedQueueId(null);
    setTaskQueueError("");
    setTaskQueueLoading(false);
    setTaskQueueApplying(false);
    setTaskQueuePanelOpen(false);
    setTaskQueueExpandedMode(false);
    setTaskQueueConnectionState("idle");
    setTaskQueueRevisionSyncing(false);
    taskQueueRevisionSyncingRef.current = false;
    lastProcessedEventKeyRef.current = null;
    if (!hasActiveSession) {
      return;
    }
    loadTaskQueueSnapshotRef.current?.({ silent: false });
  }, [
    // storeSessionDetail?.clientId is passed from the parent via the hook
    // inputs; we depend on selectedSessionId + selectedPublicSessionToken
    selectedSessionId,
    selectedPublicSessionToken,
  ]);

  // Connection state derivation
  useEffect(() => {
    const hasActiveSession =
      Boolean(selectedSessionId) && selectedSessionId !== "__pending__";
    let nextState = "idle";
    if (!hasActiveSession || !taskQueuePlannerUiEnabled) {
      nextState = "idle";
    } else if (isPublicMode) {
      nextState = selectedPublicSessionToken ? "polling" : "idle";
    } else if (privateSessionStreamState === "reconnecting") {
      nextState = "reconnecting";
    } else if (privateSessionStreamState === "connected") {
      nextState = "live";
    } else if (privateSessionStreamState === "disconnected") {
      nextState = "polling";
    }
    setTaskQueueConnectionState((prev) => {
      if (prev !== nextState) {
        if (isStreamDebugEnabled()) console.log("[TQ connState]", { from: prev, to: nextState, privateSessionStreamState });
      }
      return nextState;
    });
  }, [
    isPublicMode,
    privateSessionStreamState,
    selectedPublicSessionToken,
    selectedSessionId,
    taskQueuePlannerUiEnabled,
  ]);

  // Polling interval
  useEffect(() => {
    const hasActiveSession =
      Boolean(selectedSessionId) && selectedSessionId !== "__pending__";
    if (!hasActiveSession) return;
    if (!taskQueuePlannerUiEnabled) return;
    if (isPublicMode && !selectedPublicSessionToken) return;
    const id = setInterval(() => {
      const queueStatus = String(taskQueueSnapshotRef.current?.queue?.status || "").toUpperCase();
      const intervalMs = queueStatus === "ACTIVE" ? 5000 : 10000;
      if (isStreamDebugEnabled()) console.log("[TQ poll] tick", { queueStatus, intervalMs, willFetch: intervalMs <= 5000 });
      if (intervalMs > 5000) return;
      loadTaskQueueSnapshotRef.current?.({ silent: true });
    }, 5000);
    return () => clearInterval(id);
  }, [
    isPublicMode,
    selectedPublicSessionToken,
    selectedSessionId,
    taskQueuePlannerUiEnabled,
  ]);

  // SSE event handling
  useEffect(() => {
    if (isPublicMode) return;
    if (!taskQueuePlannerUiEnabled) return;
    if (!latestTaskQueueEvent?.eventKey) return;
    // Deduplicate: never process the same eventKey twice
    if (latestTaskQueueEvent.eventKey === lastProcessedEventKeyRef.current) {
      if (isStreamDebugEnabled()) console.log("[TQ SSE] skipping already-processed eventKey", latestTaskQueueEvent.eventKey);
      return;
    }
    lastProcessedEventKeyRef.current = latestTaskQueueEvent.eventKey;

    const eventRevision = Number(latestTaskQueueEvent.revision);
    const snap = taskQueueSnapshotRef.current;
    const localRevision = Number(snap?.queue?.revision);
    const eventQueueId = String(latestTaskQueueEvent.queueId || "").trim();
    const localQueueId = String(snap?.queue?.queueId || "").trim();
    const userSelectedQueueId = selectedQueueIdRef.current;
    const revisionsAreComparable =
      Number.isFinite(eventRevision) && Number.isFinite(localRevision);
    const isStaleEvent =
      revisionsAreComparable &&
      eventRevision < localRevision &&
      (!eventQueueId || !localQueueId || eventQueueId === localQueueId);
    const isQueueIdentityMismatch =
      Boolean(eventQueueId) &&
      Boolean(localQueueId) &&
      eventQueueId !== localQueueId;
    const hasRevisionGap =
      revisionsAreComparable && eventRevision > localRevision + 1;
    const shouldRefresh =
      isQueueIdentityMismatch ||
      !Number.isFinite(eventRevision) ||
      !Number.isFinite(localRevision) ||
      eventRevision > localRevision;

    if (isStreamDebugEnabled()) console.log("[TQ SSE] event:", {
      eventKey: latestTaskQueueEvent.eventKey,
      type: latestTaskQueueEvent.type,
      eventQueueId,
      eventRevision,
      localQueueId,
      localRevision,
      userSelectedQueueId,
      isStaleEvent,
      isQueueIdentityMismatch,
      hasRevisionGap,
      shouldRefresh,
    });

    if (isStaleEvent) {
      if (isStreamDebugEnabled()) console.log("[TQ SSE] skipping stale event");
      if (
        !taskQueuePanelOpen &&
        TASK_QUEUE_AUTO_OPEN_EVENT_TYPES.has(latestTaskQueueEvent.type)
      ) {
        setTaskQueuePanelOpen(true);
      }
      return;
    }
    if (shouldRefresh) {
      const requiresReconcile = hasRevisionGap || isQueueIdentityMismatch;
      if (isStreamDebugEnabled()) console.log("[TQ SSE] refreshing", { requiresReconcile, isQueueIdentityMismatch, hasRevisionGap });
      if (requiresReconcile) {
        setTaskQueueRevisionSyncing(true);
        taskQueueRevisionSyncingRef.current = true;
      }
      loadTaskQueueSnapshotRef.current?.({ silent: true })?.then?.((result) => {
        const fetchedQueueId = result?.queue?.queueId;
        if (isStreamDebugEnabled()) console.log("[TQ SSE] refresh result", {
          fetchedQueueId,
          userSelectedQueueId,
          willRestore: userSelectedQueueId && fetchedQueueId && userSelectedQueueId !== fetchedQueueId,
        });
        // If the user was viewing a historical (non-latest) queue,
        // restore that snapshot so the SSE refresh doesn't yank their view
        // to the active queue.
        if (
          userSelectedQueueId &&
          fetchedQueueId &&
          userSelectedQueueId !== fetchedQueueId
        ) {
          setTaskQueueHistory((prevHistory) => {
            const match = prevHistory.find(
              (s) => s?.queue?.queueId === userSelectedQueueId
            );
            if (isStreamDebugEnabled()) console.log("[TQ SSE] restore historical queue", { userSelectedQueueId, found: !!match });
            if (match) {
              setTaskQueueSnapshot(match);
              setSelectedQueueId(userSelectedQueueId);
            }
            return prevHistory;
          });
        }
      })?.finally?.(() => {
        if (taskQueueRevisionSyncingRef.current) {
          setTaskQueueRevisionSyncing(false);
          taskQueueRevisionSyncingRef.current = false;
        }
      });
    } else {
      if (isStreamDebugEnabled()) console.log("[TQ SSE] no refresh needed");
      if (taskQueueRevisionSyncingRef.current) {
        setTaskQueueRevisionSyncing(false);
        taskQueueRevisionSyncingRef.current = false;
      }
    }
    if (
      !taskQueuePanelOpen &&
      TASK_QUEUE_AUTO_OPEN_EVENT_TYPES.has(latestTaskQueueEvent.type)
    ) {
      setTaskQueuePanelOpen(true);
    }
  }, [
    isPublicMode,
    latestTaskQueueEvent?.eventKey,
    latestTaskQueueEvent?.queueId,
    latestTaskQueueEvent?.revision,
    latestTaskQueueEvent?.type,
    taskQueuePlannerUiEnabled,
    taskQueuePanelOpen,
  ]);

  // Panel open/close side effects
  useEffect(() => {
    if (!taskQueuePanelOpen) {
      setMobileQueueTab("chat");
      setTaskQueueExpandedMode(false);
    }
  }, [taskQueuePanelOpen]);

  useEffect(() => {
    if (!selectedSessionId || selectedSessionId === "__pending__") {
      setMobileQueueTab("chat");
    }
  }, [selectedSessionId]);

  return {
    // State
    taskQueuePanelOpen,
    taskQueuePanelWidth,
    taskQueueSnapshot,
    taskQueueHistory,
    selectedQueueId,
    taskQueueLoading,
    taskQueueApplying,
    taskQueueError,
    taskQueueConnectionState,
    taskQueueRevisionSyncing,
    taskQueueExpandedMode,
    mobileQueueTab,
    // Setters needed by parent
    setMobileQueueTab,
    setTaskQueuePanelWidth,
    // Callbacks
    loadTaskQueueSnapshot,
    handleApplyTaskQueueAction,
    handleSelectQueue,
    handleOpenTaskQueuePanel,
    handleCloseTaskQueuePanel,
    handleTaskQueueExpandedModeChange,
  };
}
